import { routes } from "./routes";
import {
  REPORT_EXAMPLE_DATE,
  reportExampleImage,
  reportExamplePdf,
} from "../config/reportExample";

/**
 * Flat map of legacy path -> current path.
 *
 * Source of truth for redirects, shared by the SSR server (which emits real
 * HTTP 301s — what Google honours) and the client `<Redirects>` component
 * (which keeps in-app SPA navigation consistent). Previously these mappings
 * only existed client-side via react-router `<Navigate>`, which Google treats
 * as a 200 + JS hop, so the old URLs were never consolidated and lingered in
 * Search Console as soft 404 / noindex / crawled-not-indexed phantoms.
 */
const redirectMap = new Map<string, string>();

const addTuples = (redirects?: Record<string, [string, string]>) => {
  if (!redirects) return;
  Object.values(redirects).forEach((tuple) => {
    if (Array.isArray(tuple) && tuple.length === 2) {
      redirectMap.set(tuple[0], tuple[1]);
    }
  });
};

// Curated old -> new tuples maintained in routes.ts.
Object.values(routes.redirects ?? {}).forEach(addTuples);

const currentReportVersion = REPORT_EXAMPLE_DATE.match(/^(\d{8})(?:-v(\d+))?$/);

/** Only earlier dated report captures move to today's shared example report. */
function resolveHistoricalReportAsset(path: string): string | null {
  if (!currentReportVersion) return null;
  const pdf = path.match(
    /^\/images\/analysis-report\/example-(sv|en)-(\d{8}(?:-v\d+)?)\.pdf$/,
  );
  const image = path.match(
    /^\/images\/analysis-report\/(cover|summary|plan-check)-(sv|en)-(\d{8}(?:-v\d+)?)(?:-(\d+))?\.(avif|webp|png)$/,
  );
  const version = pdf?.[2] ?? image?.[3];
  if (!version) return null;

  const historicalVersion = version.match(/^(\d{8})(?:-v(\d+))?$/);
  if (!historicalVersion) return null;
  const [, date, rawVersion] = historicalVersion;
  const [, currentDate, rawCurrentVersion] = currentReportVersion;
  const versionNumber = Number(rawVersion ?? 0);
  const currentVersionNumber = Number(rawCurrentVersion ?? 0);
  if (
    date > currentDate ||
    (date === currentDate && versionNumber >= currentVersionNumber)
  ) {
    return null;
  }

  if (pdf) return reportExamplePdf(pdf[1] as "sv" | "en");
  if (!image) return null;

  const page = image[1] as "cover" | "summary" | "plan-check";
  const locale = image[2] as "sv" | "en";
  const width = image[4] ? Number(image[4]) : undefined;
  const format = image[5];
  const descriptor = reportExampleImage(page, locale);
  if (
    width !== undefined &&
    descriptor.widths.includes(width) &&
    format !== "png"
  ) {
    if (width === descriptor.width && format === "webp") {
      return descriptor.full;
    }
    return `${descriptor.base}-${width}.${format}`;
  }
  return descriptor.full;
}

// Direct mappings that previously lived inline in Redirects.tsx.
const DIRECT: Array<[string, string]> = [
  // Technical integration guides live on the one Developers page (2026-10-27):
  // every former Help or Developers address reaches its section in one hop.
  ["/hjalp/caire-connect", "/resurser/utvecklare#connect"],
  ["/en/help/caire-connect", "/en/resources/developers#connect"],
  ["/hjalp/koppla-en-ai-assistent-mcp", "/resurser/utvecklare#mcp"],
  ["/en/help/connect-an-ai-assistant-mcp", "/en/resources/developers#mcp"],
  ["/resurser/utvecklare/connect", "/resurser/utvecklare#connect"],
  ["/en/resources/developers/connect", "/en/resources/developers#connect"],
  ["/resurser/utvecklare/mcp", "/resurser/utvecklare#mcp"],
  ["/en/resources/developers/mcp", "/en/resources/developers#mcp"],
  ["/resurser/utvecklare/api", "/resurser/utvecklare#api"],
  ["/en/resources/developers/api", "/en/resources/developers#api"],
  ["/hjalp/resurser/ask-caire", "/hjalp/ai-assistent-schema"],
  ["/en/help/resources/ask-caire", "/en/help/schedule-ai-assistant"],
  // 2026-10-03: the old example report was replaced by the redesigned one.
  [
    "/images/analysis-report/cover-20261002.png",
    reportExampleImage("cover", "sv").full,
  ],
  [
    "/images/analysis-report/cover-en-20261002.png",
    reportExampleImage("cover", "en").full,
  ],
  [
    "/images/analysis-report/summary-20261002.png",
    reportExampleImage("summary", "sv").full,
  ],
  [
    "/images/analysis-report/summary-en-20261002.png",
    reportExampleImage("summary", "en").full,
  ],
  ["/images/analysis-report/example-sv-20261002.pdf", reportExamplePdf("sv")],
  ["/images/analysis-report/example-en-20261002.pdf", reportExamplePdf("en")],
  // One page per topic (owner 2026-10-10): these feature pages were merged
  [
    "/produkt/funktioner/automatisk-schemalaggning-hemtjanst",
    "/produkt/funktioner/ai-schemalaggning",
  ],
  [
    "/en/product/features/automatic-scheduling-home-care",
    "/en/product/features/ai-scheduling",
  ],
  // into the page that now carries their content.
  [
    "/produkt/funktioner/caire-core-schemalaggning",
    "/produkt/funktioner/ai-schemalaggning",
  ],
  [
    "/en/product/features/caire-core-scheduling",
    "/en/product/features/ai-scheduling",
  ],
  ["/produkt/funktioner/mycaire", "/produkt/funktioner/mobilappar"],
  ["/en/product/features/mycaire", "/en/product/features/mobile-apps"],
  ["/produkt/funktioner/resurshantering", "/produkt/funktioner/onboarding"],
  [
    "/en/product/features/resource-management",
    "/en/product/features/onboarding",
  ],
  ["/produkt/funktioner/besokens-datalivscykel", "/plattform"],
  ["/en/product/features/visit-data-lifecycle", "/en/platform"],
  // One product overview (owner 2026-10-10): /plattform. The former product
  // and features hubs repeated a subset of it under a second and third URL.
  ["/produkter", "/plattform"],
  ["/en/products", "/en/platform"],
  ["/produkt/funktioner", "/plattform"],
  ["/en/product/features", "/en/platform"],
  ["/funktioner", "/plattform"],
  ["/en/features", "/en/platform"],
  ["/tjanster", "/plattform"],
  ["/en/services", "/en/platform"],
  ["/produkt/tjanster", "/plattform"],
  ["/en/product/services", "/en/platform"],
  ["/for-verksamheten", "/losningar"],
  ["/en/for-business", "/en/solutions"],
  // Repointed to the homepage 2026-09-05, and this one is worth the comment.
  // `/funktioner/schemaläggning` is a pre-2026 URL that held **position 5.4–9.1
  // for nine months** on "automatisk schemaläggning" — 20,270 impressions and 27
  // clicks over the last year, as many clicks as the Carefox page earned from
  // 22,425. It and its replacement sat in the index together from Sep 2025; when
  // it finally dropped out in June 2026 the ranking did not transfer, because
  // the 301 target is an *AI-scheduling* page by URL and title and sits at 32.
  // The homepage is titled and H1'd "Automatisk schemaläggning för hemtjänsten"
  // — the exact query — and carries the site's authority, so that is where this
  // URL's equity belongs. Every inbound customer so far came from this query,
  // `AI-schemaläggning`, or `ruttoptimering` (owner, 2026-09-05).
  // The `/produkt/funktioner/schemal*` variants below keep the product target:
  // they are product-namespace URLs and have never drawn an impression.
  ["/funktioner/schemaläggning", "/"],
  ["/funktioner/schemalaggning", "/"],
  [
    "/produkt/funktioner/schemaläggning",
    "/produkt/funktioner/ai-schemalaggning",
  ],
  [
    "/produkt/funktioner/schemalaggning",
    "/produkt/funktioner/ai-schemalaggning",
  ],
  ["/en/features/scheduling", "/en/product/features/ai-scheduling"],
  ["/funktioner/analysverktyg", "/produkt/funktioner/analysverktyg"],
  ["/en/features/analytics", "/en/product/features/analytics"],
  // The 2025 whitepaper PDF was unpublished: uncited third-party figures
  // and stale content (no Timefold, no two-layer architecture, no Ask
  // Caire, no mobile apps). The HTML customer presentation replaces it,
  // and the PDF URL was emailed around, so it redirects rather than 404s.
  [
    "/downloads/Caire-A-new-platform-for-Home-Care-powered-by-AI.pdf",
    "/en/resources/customer-presentation/caire-for-hemtjanst.html",
  ],
  ["/resurser/whitepapers", "/om-oss"],
  ["/en/resources/whitepapers", "/en/about"],
  [
    "/funktioner/ruttoptimering",
    "/produkt/funktioner/ruttoptimering-hemtjanst",
  ],
  [
    "/en/features/route-optimization",
    "/en/product/features/route-optimization-home-care",
  ],
  // Integrations was never a shipped product catalogue. Retire the feature URL
  // into Help Center onboarding (standing data before the first schedule).
  // These were kept on one line because generate-sitemap.js could not parse a
  // multi-line tuple; prettier reformatted them anyway and five silently fell
  // out of the sitemap's exclusion set. The parser handles both shapes now, so
  // the formatting here is free.
  [
    "/produkt/funktioner/integrationer",
    "/hjalp/sa-fungerar-caire-innan-forsta-schemat",
  ],
  [
    "/en/product/features/integrations",
    "/en/help/how-caire-works-before-first-schedule",
  ],
  [
    "/funktioner/integrationer",
    "/hjalp/sa-fungerar-caire-innan-forsta-schemat",
  ],
  [
    "/en/features/integrations",
    "/en/help/how-caire-works-before-first-schedule",
  ],
  [
    "/product/features/integrations",
    "/en/help/how-caire-works-before-first-schedule",
  ],
  // The Ask CAIRE FAQ and /vanliga-fragor served the same purpose from two
  // different corpora. /vanliga-fragor is the URL the nav, footer, contact and
  // 404 pages all point at and the one with search history, so it became the
  // single FAQ — rendered from the same catalog the chat answers from.
  ["/fraga-caire", "/vanliga-fragor"],
  ["/en/ask-caire", "/en/faq"],
  // Public marketing copy never names vendors or partners (owner, 2026-10-24).
  // The two vendor-named pages were retired into their neutral equivalents:
  // the AI-scheduling feature page and the buyer's-guide comparison.
  [
    "/produkt/funktioner/ai-schemalaggning-carefox",
    "/produkt/funktioner/ai-schemalaggning",
  ],
  [
    "/en/product/features/ai-scheduling-carefox",
    "/en/product/features/ai-scheduling",
  ],
  [
    "/product/features/ai-scheduling-carefox",
    "/en/product/features/ai-scheduling",
  ],
  [
    "/resurser/jamforelser/carefox-vs-caire",
    "/resurser/jamforelser/jamforelse-schemalaggningssystem-hemtjanst",
  ],
  [
    "/en/resources/comparisons/carefox-vs-caire",
    "/en/resources/comparisons/home-care-scheduling-tools-comparison",
  ],
  // Nuked marketing schedule excerpt — one Nacka demo only (in-app DB Demo SA).
  ["/demo/schedule", "https://app.caire.se/welcome"],
  ["/en/demo/schedule", "https://app.caire.se/welcome"],
];
DIRECT.forEach(([from, to]) => redirectMap.set(from, to));

// English content that Google indexed WITHOUT the /en locale prefix. Every
// target below is a verified route in routes.ts (the source path is not), so
// these are safe 1:1 redirects onto the canonical /en URL.
const EN_AT_ROOT: string[] = [
  "/about",
  "/calculator",
  "/careers",
  "/contact",
  "/faq",
  "/how-caire-works",
  "/pricing",
  "/privacy",
  "/resources",
  "/what-is-caire",
  "/product/features/ai-scheduling",
  "/product/features/ai-staff-planning-home-care",
  "/product/features/analytics",
  "/product/features/onboarding",
  "/product/features/security-compliance",
  "/resources/articles",
  "/resources/articles/80-percent-efficiency-home-care",
  "/resources/articles/balancing-efficiency-continuity",
  "/resources/articles/constraint-management-homecare-scheduling",
  "/resources/articles/continuity-efficiency-ai",
  "/resources/articles/optimization-continuity-algorithms",
  "/resources/articles/vrptw-home-care-optimization",
  "/resources/comparisons/excel-vs-ai",
  "/resources/comparisons/home-care-scheduling-tools-comparison",
  "/resources/guides",
  "/resources/guides/optimize-home-care-schedule",
  "/terms",
  "/solutions",
  "/solutions/home-care-chain",
  "/solutions/private-home-care",
];
EN_AT_ROOT.forEach((path) => redirectMap.set(path, `/en${path}`));
redirectMap.set("/products", "/en/platform");
redirectMap.set("/product/features", "/en/platform");
redirectMap.set(
  "/product/features/mycaire",
  "/en/product/features/mobile-apps",
);
redirectMap.set("/resources/whitepapers", "/en/about");

// Google also crawled the public help centre through English-at-root URLs.
// Keep the whole help collection consolidated onto the canonical /en tree.
Object.values(routes.help).forEach((tuple) => {
  const englishPath = tuple[1];
  redirectMap.set(englishPath.replace(/^\/en/, ""), englishPath);
});

// Curated legacy paths from past URL restructures (Swedish↔English slug
// renames, /en + Swedish-slug combos, retired blog/article slugs). Every target
// is a verified current route; retired leaves whose exact replacement no longer
// exists point at the nearest section index so they 301 rather than 404.
const LEGACY_CURATED: Array<[string, string]> = [
  // Auth URLs belong to the dashboard app; keep old marketing-host paths out
  // of Search Console's 404 bucket while preserving conversion intent.
  ["/sign-in", "https://app.caire.se/sign-in"],
  ["/sign-up", "https://app.caire.se/sign-up"],
  // /en resources (legacy slugs)
  ["/en/resources/ai-scheduling-revolution", "/en/resources"],
  [
    "/en/resources/articles/future-homecare-2030",
    "/en/resources/articles/future-home-care-trends",
  ],
  [
    "/en/resources/future-homecare-trends",
    "/en/resources/articles/future-home-care-trends",
  ],
  [
    "/en/resources/guides/effective-staff-planning",
    "/en/resources/guides/effective-staff-planning-home-care",
  ],
  ["/en/resurser", "/en/resources"],
  ["/en/resurser/artiklar", "/en/resources/articles"],
  [
    "/en/resurser/artiklar/80-procent-brukartid-hemtjanst",
    "/en/resources/articles/80-percent-efficiency-home-care",
  ],
  [
    "/en/resurser/artiklar/ai-i-hemtjansten-risk-eller-revolution",
    "/en/resources/articles/comprehensive-ai-in-home-care",
  ],
  // /en twin of the SV /resources/articles/ai-in-home-care-risk-or-revolution
  // entry below — Google also indexed this /en variant (Search Console noindex).
  [
    "/en/resources/articles/ai-in-home-care-risk-or-revolution",
    "/en/resources/articles/comprehensive-ai-in-home-care",
  ],
  [
    "/en/resurser/artiklar/balansera-effektivitet-kontinuitet",
    "/en/resources/articles/balancing-efficiency-continuity",
  ],
  [
    "/en/resurser/artiklar/framtidens-hemtjanst-trender",
    "/en/resources/articles/future-home-care-trends",
  ],
  [
    "/en/resurser/artiklar/kontinuitet-effektivitet-ai",
    "/en/resources/articles/continuity-efficiency-ai",
  ],
  [
    "/en/resurser/artiklar/omfattande-guide-ai-i-hemtjansten",
    "/en/resources/articles/comprehensive-ai-in-home-care",
  ],
  [
    "/en/resurser/artiklar/optimering-kontinuitet-algoritmer",
    "/en/resources/articles/optimization-continuity-algorithms",
  ],
  [
    "/en/resurser/artiklar/vrptw-hemtjanst-optimering",
    "/en/resources/articles/vrptw-home-care-optimization",
  ],
  ["/en/resurser/effektivisera-hemtjanst-ai", "/en/resources"],
  ["/en/resurser/guider", "/en/resources/guides"],
  [
    "/en/resurser/guider/comprehensive-ai-implementation-guide",
    "/en/resources/guides/comprehensive-ai-implementation",
  ],
  [
    "/en/resurser/guider/effektiv-personalplanering-hemtjanst",
    "/en/resources/guides/effective-staff-planning-home-care",
  ],
  [
    "/en/resurser/guider/optimera-hemtjanst-schema",
    "/en/resources/guides/optimize-home-care-schedule",
  ],
  ["/en/hjalp", "/en/help"],
  ["/en/hjalp/losningar-och-uppfoljning", "/en/help/solutions-and-follow-up"],
  [
    "/en/hjalp/medarbetardata-for-rutt-och-kostnad",
    "/en/help/employee-effective-inputs",
  ],
  ["/en/hjalp/optimeringsberedskap", "/en/help/optimization-readiness"],
  ["/en/resurser/jamforelser", "/en/resources/comparisons"],
  [
    "/en/resurser/jamforelser/excel-vs-ai",
    "/en/resources/comparisons/excel-vs-ai",
  ],
  [
    "/en/resurser/jamforelser/jamforelse-schemalaggningssystem-hemtjanst",
    "/en/resources/comparisons/home-care-scheduling-tools-comparison",
  ],
  ["/en/resurser/whitepapers", "/en/about"],
  // /en product & features (legacy slugs)
  [
    "/en/features/ai-personalplanering",
    "/en/product/features/ai-staff-planning-home-care",
  ],
  ["/en/features/ai-scheduling-carefox", "/en/product/features/ai-scheduling"],
  [
    "/en/features/ai-schemalaggning-carefox",
    "/en/product/features/ai-scheduling",
  ],
  [
    "/en/features/ai-staff-planning",
    "/en/product/features/ai-staff-planning-home-care",
  ],
  ["/en/features/analytics", "/en/product/features/analytics"],
  [
    "/en/features/onboarding-caire-carefox",
    "/en/product/features/ai-scheduling",
  ],
  [
    "/en/features/ruttoptimering-hemtjanst",
    "/en/product/features/route-optimization-home-care",
  ],
  [
    "/en/features/route-optimization-homecare",
    "/en/product/features/route-optimization-home-care",
  ],
  [
    "/en/funktioner/ruttoptimering-hemtjanst",
    "/en/product/features/route-optimization-home-care",
  ],
  ["/en/funktioner/schemaläggning", "/en/product/features/ai-scheduling"],
  ["/en/produkt/ai-schemalaggning", "/en/product/features/ai-scheduling"],
  ["/en/produkt/funktioner", "/en/platform"],
  [
    "/en/produkt/funktioner/ai-personalplanering-hemtjanst",
    "/en/product/features/ai-staff-planning-home-care",
  ],
  [
    "/en/produkt/funktioner/ai-schemalaggning",
    "/en/product/features/ai-scheduling",
  ],
  [
    "/en/produkt/funktioner/ai-schemalaggning-carefox",
    "/en/product/features/ai-scheduling",
  ],
  ["/en/produkt/funktioner/analysverktyg", "/en/product/features/analytics"],
  [
    "/en/produkt/funktioner/integrationer",
    "/en/help/how-caire-works-before-first-schedule",
  ],
  ["/en/produkt/funktioner/mycaire", "/en/product/features/mobile-apps"],
  ["/en/produkt/funktioner/onboarding", "/en/product/features/onboarding"],
  [
    "/en/produkt/funktioner/onboarding-caire-carefox",
    "/en/product/features/ai-scheduling",
  ],
  [
    "/en/produkt/funktioner/ruttoptimering-hemtjanst",
    "/en/product/features/route-optimization-home-care",
  ],
  [
    "/en/produkt/funktioner/sakerhet-efterlevnad",
    "/en/product/features/security-compliance",
  ],
  ["/en/produkt/funktioner/ai-compliance", "/en/trust/eu-ai-act"],
  ["/en/produkt/mycaire", "/en/product/features/mobile-apps"],
  ["/en/product/mycaire", "/en/product/features/mobile-apps"],
  [
    "/en/produkt/sakerhet-efterlevnad",
    "/en/product/features/security-compliance",
  ],
  [
    "/en/product/security-compliance",
    "/en/product/features/security-compliance",
  ],
  [
    "/en/produkt/sakerhet-efterlevnad/ai",
    "/en/product/features/security-compliance",
  ],
  [
    "/en/product/security-compliance/ai",
    "/en/product/features/security-compliance",
  ],
  ["/en/produkt/tjanster", "/en/platform"],
  ["/en/produkt/tjanster/extra-integrationer", "/en/platform"],
  ["/en/produkt/tjanster/personalhandbok", "/en/platform"],
  ["/en/produkt/tjanster/webb-utveckling", "/en/platform"],
  ["/en/produkter", "/en/platform"],
  // /en solutions (legacy slugs)
  ["/en/losningar", "/en/solutions"],
  ["/en/losningar/hemtjanstkedja", "/en/solutions/home-care-chain"],
  ["/en/losningar/privat-hemtjanst", "/en/solutions/private-home-care"],
  ["/en/losningar/verksamhetschef", "/en/solutions/operations-manager"],
  // /en single pages (Swedish slugs)
  ["/en/anvandarvillkor", "/en/terms"],
  ["/en/integritetspolicy", "/en/privacy"],
  ["/en/kalkylator", "/en/calculator"],
  ["/en/karriar", "/en/careers"],
  ["/en/kontakt", "/en/contact"],
  ["/en/kontakta-oss", "/en/contact"],
  ["/en/om-oss", "/en/about"],
  ["/en/prissattning", "/en/pricing"],
  ["/en/privacy-policy", "/en/privacy"],
  ["/en/product", "/en/platform"],
  ["/en/sa-fungerar-caire", "/en/how-caire-works"],
  ["/en/vad-ar-caire", "/en/what-is-caire"],
  ["/en/vanliga-fragor", "/en/faq"],
  ["/en/villkor", "/en/terms"],
  // SV resources & blog (legacy)
  ["/blogg/ai-schemalagning-framtiden", "/resurser/artiklar"],
  ["/blogg/kostnadseffektiv-hemtjanst", "/resurser/artiklar"],
  [
    "/resurser/ai-personalplanering-hemtjanst",
    "/resurser/guider/effektiv-personalplanering-hemtjanst",
  ],
  ["/resurser/ai-schemalaggning-carefox", "/resurser"],
  ["/resurser/ai-schemalagning-roi-berakning", "/resurser"],
  ["/resurser/ai-schemaläggning-revolution", "/resurser"],
  [
    "/resurser/artiklar/ai-i-hemtjansten-risk-eller-revolution",
    "/resurser/artiklar/omfattande-guide-ai-i-hemtjansten",
  ],
  [
    "/resurser/artiklar/framtidens-hemtjanst-2030",
    "/resurser/artiklar/framtidens-hemtjanst-trender",
  ],
  [
    "/resurser/framtidens-hemtjanst-trender",
    "/resurser/artiklar/framtidens-hemtjanst-trender",
  ],
  ["/resurser/implementeringsguide", "/resurser/guider"],
  ["/resurser/personal-rekrytering-hemtjanst", "/resurser"],
  ["/resurser/steg-for-steg-ai-schemaläggning", "/resurser"],
  // SV / English-at-root product (legacy)
  ["/features/ai-schemalaggning-carefox", "/en/product/features/ai-scheduling"],
  [
    "/features/ai-personalplanering",
    "/produkt/funktioner/ai-personalplanering-hemtjanst",
  ],
  [
    "/features/ai-staff-planning",
    "/en/product/features/ai-staff-planning-home-care",
  ],
  ["/features/onboarding-caire-carefox", "/en/product/features/ai-scheduling"],
  [
    "/features/route-optimization-home-care",
    "/en/product/features/route-optimization-home-care",
  ],
  [
    "/features/route-optimization-homecare",
    "/en/product/features/route-optimization-home-care",
  ],
  [
    "/features/ruttoptimering-hemtjanst",
    "/produkt/funktioner/ruttoptimering-hemtjanst",
  ],
  [
    "/funktioner/ai-personalplanering-hemtjanst",
    "/produkt/funktioner/ai-personalplanering-hemtjanst",
  ],
  [
    "/funktioner/ai-schemalaggning-carefox",
    "/produkt/funktioner/ai-schemalaggning",
  ],
  [
    "/funktioner/onboarding-caire-carefox",
    "/produkt/funktioner/ai-schemalaggning",
  ],
  [
    "/funktioner/routeoptimization",
    "/produkt/funktioner/ruttoptimering-hemtjanst",
  ],
  ["/produkt", "/plattform"],
  ["/produkt/ai-schemalaggning", "/produkt/funktioner/ai-schemalaggning"],
  ["/produkt/mycaire", "/produkt/funktioner/mobilappar"],
  ["/produkt/sakerhet-efterlevnad", "/produkt/funktioner/sakerhet-efterlevnad"],
  [
    "/produkt/sakerhet-efterlevnad/ai",
    "/produkt/funktioner/sakerhet-efterlevnad",
  ],
  // misc root (legacy)
  ["/features", "/en/platform"],
  ["/help", "/hjalp"],
  ["/help/getting-started", "/hjalp"],
  ["/kontakta-oss", "/kontakt"],
  ["/en/", "/en"],
  ["/platform", "/en/platform"],
  ["/privacy-policy", "/integritetspolicy"],
  ["/product", "/en/platform"],
  ["/product/mycaire", "/en/product/features/mobile-apps"],
  ["/product/security-compliance", "/en/product/features/security-compliance"],
  [
    "/product/security-compliance/ai",
    "/en/product/features/security-compliance",
  ],
  ["/product/features/ai-compliance", "/en/trust/eu-ai-act"],
  [
    "/product/features/onboarding-caire-carefox",
    "/en/product/features/ai-scheduling",
  ],
  ["/produkt/funktioner/ai-compliance", "/trygghet/eu-ai-act"],
  [
    "/produkt/funktioner/onboarding-caire-carefox",
    "/produkt/funktioner/ai-schemalaggning",
  ],
  ["/en/product/features/ai-compliance", "/en/trust/eu-ai-act"],
  [
    "/en/product/features/onboarding-caire-carefox",
    "/en/product/features/ai-scheduling",
  ],
  ["/product/services", "/en/platform"],
  ["/product/services/extra-integrations", "/en/platform"],
  ["/product/services/web-dev", "/en/platform"],
  [
    "/resources/articles/ai-in-home-care-risk-or-revolution",
    "/en/resources/articles/comprehensive-ai-in-home-care",
  ],
  [
    "/resources/implementation-guide",
    "/en/resources/guides/comprehensive-ai-implementation",
  ],
  ["/villkor", "/anvandarvillkor"],
];
LEGACY_CURATED.forEach(([from, to]) => redirectMap.set(from, to));

/**
 * Resolve a request path to its redirect target, or null if none.
 * Trailing slashes (other than root) are normalised so `/foo/` and `/foo`
 * resolve identically.
 */
export function resolveRedirect(pathname: string): string | null {
  const stripTrailingSlash = (path: string) =>
    path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path;

  const safeDecodePath = (path: string) => {
    try {
      return decodeURI(path);
    } catch {
      return path;
    }
  };

  const normalized = stripTrailingSlash(pathname);
  const decoded = safeDecodePath(pathname);
  const normalizedDecoded =
    decoded === pathname ? normalized : stripTrailingSlash(decoded);

  const candidates = [normalized, pathname, normalizedDecoded, decoded];

  for (const candidate of candidates) {
    const reportTarget = resolveHistoricalReportAsset(candidate);
    if (reportTarget) return reportTarget;

    const target = redirectMap.get(candidate);
    if (target) return target;

    const helpTarget = resolveLegacyHelpPath(candidate);
    if (helpTarget) return helpTarget;
  }

  return null;
}

/**
 * The public help center moved out of Resources on 2026-10-10
 * (`/resurser/hjalp` → `/hjalp`, `/en/resources/help` → `/en/help`) so product
 * help no longer shares a section with home-care planning guides. Every old
 * help URL, including slugs added after this list was written, keeps its
 * equity through one 301 hop.
 */
// Prefix rules, not exact redirects: kept as objects so the sitemap
// generator's tuple parser does not read them as exact sources.
const LEGACY_HELP_PREFIXES: ReadonlyArray<{ from: string; to: string }> = [
  { from: "/resurser/hjalp", to: "/hjalp" },
  { from: "/en/resources/help", to: "/en/help" },
  // Unprefixed English and Swedish-under-/en variants that were already legacy.
  { from: "/resources/help", to: "/en/help" },
  { from: "/en/resurser/hjalp", to: "/en/hjalp" },
];

/**
 * Help centre by planning flow (2026-10-27, wiki/plans/help-center-lifecycle-facets-2026-10-27.md):
 * the six category pages and the `/resurser` field references moved. Each
 * old address reaches its new home in one hop.
 */
const HELP_CATEGORY_TARGETS: Record<"sv" | "en", Record<string, string>> = {
  sv: {
    planning: "/hjalp#planeringsflodet",
    articles: "/hjalp/artiklar",
    resources: "/hjalp/data",
    glossary: "/hjalp/matvarden-och-begrepp",
    ai: "/hjalp/ai",
    support: "/hjalp/vanliga-fragor",
  },
  en: {
    planning: "/en/help#planeringsflodet",
    articles: "/en/help/articles",
    resources: "/en/help/data",
    glossary: "/en/help/metrics-and-terms",
    ai: "/en/help/ai",
    support: "/en/help/faq",
  },
};
const HELP_DATA_PREFIXES: ReadonlyArray<{ from: string; to: string }> = [
  { from: "/hjalp/resurser", to: "/hjalp/data" },
  { from: "/en/help/resources", to: "/en/help/data" },
];

function resolveHelpRestructure(path: string): string | null {
  const category = /^\/(?:hjalp\/kategori|en\/help\/category)\/([^/]+)$/.exec(
    path,
  );
  if (category) {
    const locale = path.startsWith("/en/") ? "en" : "sv";
    return HELP_CATEGORY_TARGETS[locale][category[1]!] ?? null;
  }
  for (const { from, to } of HELP_DATA_PREFIXES) {
    if (path === from || path.startsWith(`${from}/`))
      return `${to}${path.slice(from.length)}`;
  }
  return null;
}

function resolveLegacyHelpPath(path: string): string | null {
  const restructured = resolveHelpRestructure(path);
  if (restructured) return restructured;
  for (const { from, to } of LEGACY_HELP_PREFIXES) {
    if (path !== from && !path.startsWith(`${from}/`)) continue;
    const moved = `${to}${path.slice(from.length)}`;
    const curated = redirectMap.get(moved);
    if (curated) return curated;
    const relocated = resolveHelpRestructure(moved);
    if (relocated) return relocated;
    // Swedish slugs under /en have no English page of their own.
    return to === "/en/hjalp" ? "/en/help" : moved;
  }
  return null;
}

export { redirectMap };
